import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  cn,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { AyarlarSekmeleri } from './ayarlar-sekmeler'

type Role = components['schemas']['RoleRead']
type PermissionEntry = components['schemas']['PermissionCatalogEntry']

/** Yeni rol için boş taslak; düzenleme ile aynı formu kullanıyoruz. */
const YENI: Role = {
  code: '',
  name: '',
  description: null,
  permissions: [],
  is_system: false,
}

/** Ayarlar › Roller — YES-115. */
export function Roller() {
  const slug = useTenantSlug()
  const [duzenlenen, setDuzenlenen] = useState<Role | null>(null)

  const roller = useQuery({
    queryKey: ['roles', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles')
      return data ?? []
    },
  })

  const katalog = useQuery({
    queryKey: ['permission-catalog', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/permissions/catalog')
      return data ?? []
    },
  })

  const benimIzinlerim = useQuery({
    queryKey: ['me', 'permissions', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/me/permissions')
      return data ?? []
    },
  })

  if (roller.isError) {
    const yetkisiz = roller.error instanceof ApiError && roller.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz ? 'Rolleri görme yetkiniz yok.' : 'Roller yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  return (
    <Sayfa>
      <Card>
        <CardHeader
          title="Roller"
          description="Hazır roller silinemez ve adları değişmez; izinleri kısılabilir."
          action={<Button onClick={() => setDuzenlenen(YENI)}>Rol oluştur</Button>}
        />
        <CardBody>
          {roller.data && roller.data.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {roller.data.map((rol) => (
                <li key={rol.code}>
                  <button
                    type="button"
                    onClick={() => setDuzenlenen(rol)}
                    className="flex w-full items-center gap-3 rounded-[var(--radius-control)] border border-[var(--ad-border)] px-3 py-2.5 text-left hover:bg-[var(--ad-surface-muted)]"
                  >
                    <span className="flex flex-col">
                      <span className="text-sm font-medium">{rol.name}</span>
                      <span className="text-xs text-[var(--ad-text-muted)]">
                        {rol.description ?? rol.code}
                      </span>
                    </span>
                    <span className="ml-auto flex items-center gap-2">
                      {rol.is_system && <Badge tone="neutral">Hazır rol</Badge>}
                      <Badge tone="info">{rol.permissions.length} izin</Badge>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<Icon name="shield-alert" size={24} aria-hidden />}
              title="Rol yok"
              description="Hazır roller dernek açılırken oluşturulur."
            />
          )}
        </CardBody>
      </Card>

      {duzenlenen && katalog.data && (
        <RolDuzenle
          rol={duzenlenen}
          katalog={katalog.data}
          verilebilirIzinler={new Set(benimIzinlerim.data ?? [])}
          slug={slug}
          onKapat={() => setDuzenlenen(null)}
        />
      )}
    </Sayfa>
  )
}

function RolDuzenle({
  rol,
  katalog,
  verilebilirIzinler,
  slug,
  onKapat,
}: {
  rol: Role
  katalog: PermissionEntry[]
  verilebilirIzinler: Set<string>
  slug: string | null
  onKapat: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const yeni = rol.code === ''
  const [secili, setSecili] = useState<Set<string>>(new Set(rol.permissions))
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const gruplar = useMemo(() => {
    const harita = new Map<string, { label: string; izinler: PermissionEntry[] }>()
    katalog.forEach((entry) => {
      const grup = harita.get(entry.group) ?? { label: entry.group_label, izinler: [] }
      grup.izinler.push(entry)
      harita.set(entry.group, grup)
    })
    return [...harita.entries()]
  }, [katalog])

  const tazele = () => {
    void queryClient.invalidateQueries({ queryKey: ['roles', slug] })
    void queryClient.invalidateQueries({ queryKey: ['me', 'permissions', slug] })
  }

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      const izinler = [...secili]
      if (yeni) {
        await api.POST('/api/v1/roles', {
          body: {
            code: String(form.get('code')),
            name: String(form.get('name')),
            description: String(form.get('description') || '') || null,
            permissions: izinler,
          },
        })
        return
      }
      await api.PATCH('/api/v1/roles/{code}', {
        params: { path: { code: rol.code } },
        body: {
          // Hazır rolde ad DEĞİŞMEZ; sunucu da reddediyor, form da göndermiyor.
          name: rol.is_system ? undefined : String(form.get('name')),
          description: String(form.get('description') || '') || null,
          permissions: izinler,
        },
      })
    },
    onSuccess: () => {
      tazele()
      onKapat()
      toast({ title: yeni ? 'Rol oluşturuldu' : 'Rol güncellendi', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    },
  })

  const sil = useMutation({
    mutationFn: async () => {
      await api.DELETE('/api/v1/roles/{code}', { params: { path: { code: rol.code } } })
    },
    onSuccess: () => {
      tazele()
      onKapat()
      toast({ title: 'Rol silindi', tone: 'success' })
    },
    onError: (error) =>
      toast({
        title: 'Rol silinemedi',
        // Sunucu sebebi söylüyor: hazır rol, ya da kullanıcılara atanmış.
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const degistir = (deger: string, acik: boolean) =>
    setSecili((onceki) => {
      const sonraki = new Set(onceki)
      if (acik) sonraki.add(deger)
      else sonraki.delete(deger)
      return sonraki
    })

  const verilemeyenSecili = [...secili].filter((izin) => !verilebilirIzinler.has(izin))

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title={yeni ? 'Rol oluştur' : rol.name}
      description={
        rol.is_system
          ? 'Hazır rol: adı değiştirilemez, silinemez. İzinleri kısabilirsiniz.'
          : 'Rolün adını ve izinlerini düzenleyin.'
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          kaydet.mutate(new FormData(event.currentTarget))
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Rol adı" required={!rol.is_system} error={hatalar.name}>
            {(props) => (
              <Input
                name="name"
                defaultValue={rol.name}
                disabled={rol.is_system}
                autoFocus={!rol.is_system}
                {...props}
              />
            )}
          </Field>
          {yeni && (
            <Field
              label="Kod"
              required
              error={hatalar.code}
              hint="Küçük harf ve alt çizgi. Sonradan değişmez."
            >
              {(props) => <Input name="code" placeholder="etkinlik_sorumlusu" {...props} />}
            </Field>
          )}
        </div>

        <Field label="Açıklama" error={hatalar.description}>
          {(props) => <Input name="description" defaultValue={rol.description ?? ''} {...props} />}
        </Field>

        {/* Yetki yükseltme koruması: sunucu, kendinde olmayan izni verene 403
            döndürüyor. Kullanıcıyı o hataya sürüklemek yerine, veremeyeceği
            izinler burada pasif ve SEBEBİ yazılı. */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">İzinler</p>
            <span className="text-xs text-[var(--ad-text-muted)]">{secili.size} seçili</span>
          </div>

          <div className="flex max-h-72 flex-col gap-3 overflow-y-auto pr-1">
            {gruplar.map(([grupKodu, grup]) => (
              <fieldset key={grupKodu} className="flex flex-col gap-1.5">
                <legend className="pb-1 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                  {grup.label}
                </legend>
                {grup.izinler.map((izin) => {
                  const verilebilir = verilebilirIzinler.has(izin.value)
                  return (
                    <label
                      key={izin.value}
                      className={cn(
                        'flex items-start gap-2 rounded-[var(--radius-control)] px-2 py-1.5',
                        verilebilir ? 'hover:bg-[var(--ad-surface-muted)]' : 'opacity-55',
                      )}
                      title={verilebilir ? undefined : 'Sizde olmayan bir izni veremezsiniz.'}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={secili.has(izin.value)}
                        disabled={!verilebilir}
                        onChange={(event) => degistir(izin.value, event.target.checked)}
                      />
                      <span className="flex flex-col">
                        <span className="text-sm">{izin.label}</span>
                        <span className="text-xs text-[var(--ad-text-muted)]">
                          {izin.description}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </fieldset>
            ))}
          </div>

          {verilemeyenSecili.length > 0 && (
            <p className="text-xs text-[var(--ad-warning)]">
              Bu rolde sizde olmayan {verilemeyenSecili.length} izin var; onlara dokunamazsınız ama
              kaydettiğinizde korunurlar.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--ad-border)] pt-3">
          {!yeni && !rol.is_system && (
            <Button
              type="button"
              variant="ghost"
              disabled={sil.isPending}
              onClick={() => sil.mutate()}
            >
              Sil
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="secondary" onClick={onKapat}>
              Vazgeç
            </Button>
            <Button type="submit" disabled={kaydet.isPending}>
              {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  )
}

function Sayfa({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ayarlar</h1>
      <AyarlarSekmeleri />
      {children}
    </div>
  )
}
